/** Shape every PostHog experiment entry must satisfy. */
interface PosthogExperiment {
  /** Feature-flag key backing the experiment in PostHog. */
  name: string;
  /** Variant keys as configured on the flag; "control" is PostHog's default control key. */
  variants: { control: "control" } & Record<string, string>;
}

// Consuming-hook pattern (useExperimentVariant across the analytics impls, bundle-isolated per
// VITE_ANALYTICS_TOOL): see the retired "move-google-registration-button" experiment in git history.
export const EXPERIMENTS = {
  freeBallsAutoDisplay: {
    // PostHog flag backing experiments 95719 (prod) / 95716 (staging): auto-open the Get Free
    // Balls popup after the last ball (KZ-7105). Market targeting lives in the flag conditions.
    name: "free-balls-auto-display",
    variants: {
      control: "control",
      autoDisplay: "auto-display",
    },
  },
  /**
   * TK-918: referral link social preview image (KZ-7106). Brand-level test, not a
   * per-user split — flag id 275583 / experiment 96212. `logoOnly`/`bannerCta` are
   * attribution tags only, read for their `$feature/...` event property, not to
   * pick an image at runtime (the preview image itself is static per brand build).
   */
  referralLinkPreviewImage: {
    name: "referral-link-preview-image",
    variants: {
      control: "control",
      logoOnly: "logo-only",
      bannerCta: "banner-cta",
    },
  },
  usernameSuggestions: {
    name: "username-suggestions",
    variants: {
      control: "control",
      suggestionsAfterFail: "suggestions-after-fail",
    },
  },
  /**
   * TK-965 / KZ-7263: PWA install nudge on the deposit upload-slip screen (TH only).
   * Experiments 97769 (prod) / 97768 (staging). Exposure is read on entering the
   * upload-slip screen in both arms, never for players already on the PWA.
   */
  installNudgeDepositSlip: {
    name: "install-nudge-deposit-slip",
    variants: {
      control: "control",
      nudge: "install-nudge",
    },
  },
  /**
   * TK-978 / KZ-7369: PWA install toast after every winning game exit, once its VIP / auto-claim popups are done.
   * Experiments 99420 (prod) / 99417 (staging). Read the flag only when the toast would show.
   */
  installToastRewardClaim: {
    name: "install-toast-reward-claim",
    variants: {
      control: "control",
      installToast: "install-toast",
    },
  },
  /**
   * Profile tab badge + clearer Download App row for players who dismissed the install banner.
   * Exposure is read at bottom-nav render, only after every eligibility check passes — see
   * useProfileBadgeDownloadRow. `badgeRowFreeBalls` also grants 3 Free Balls on the first PWA open.
   */
  profileBadgeDownloadRow: {
    name: "profile-badge-download-row",
    variants: {
      control: "control",
      badgeRow: "badge-row",
      badgeRowFreeBalls: "badge-row-free-balls",
    },
  },
  depositNotifyAsk: {
    name: "deposit-notify-ask",
    variants: {
      control: "control",
      notifyMe: "notify-me",
    },
  },
  depositFullPage: {
    name: "deposit-full-page",
    variants: {
      control: "control",
      fullPage: "full-page",
    },
  },
  vipQuickDeposit: {
    name: "vip-quick-deposit",
    variants: {
      control: "control",
      quickDeposit: "quick-deposit",
    },
  },
  depositMergedMethodAmount: {
    name: "merge-method-amount-deposit",
    variants: {
      control: "control",
      drawer: "drawer",
      fullPage: "full-page",
    },
  },
  /**
   * TK-1068 / KZ-7605: display-only Jackpot and +3 Free Ball rows on the Free Spin reel.
   * Experiments 101696 (prod) / 101695 (staging). Read only once the wheel shows with a spin available.
   */
  freeSpinNonCashReward: {
    name: "free-spin-add-non-cash-reward",
    variants: {
      control: "control",
      jackpotFreeBall: "jackpot-free-ball",
    },
  },
  personalHourDepositDrawer: {
    name: "personal-hour-deposit-drawer",
    variants: {
      control: "control",
      drawer: "drawer",
    },
  },
  /**
   * TK-1100: 24 h "Welcome Boost" countdown for new players, no reward. Exposure is read on the first
   * member page in both arms while the boost runs. Markets and the "registered_at after launch" rule live in the flag.
   */
  countdownBoostNoGift: {
    name: "countdown-boost-no-gift",
    variants: {
      control: "control",
      countdownBoost: "countdown-boost",
    },
  },
  pwaInstallBannerStyle: {
    name: "pwa-install-banner-white-coin",
    variants: {
      control: "control",
      whiteBanner: "white-banner",
      whiteBannerCoin: "white-banner-coin",
    },
  },
  /** TK-1052. Prod 101427 (mx96g1), staging 101426 (xo268). MP-only. */
  playForFunDemoMode: {
    name: "play-for-fun-demo-mode",
    variants: {
      control: "control",
      plaque: "play_for_fun_plaque",
      sticker: "play_for_fun_sticker",
    },
  },
  /**
   * TK-1113 / KZ-7770: reward tiles on the Promotion-page notification drawer.
   * Experiment 105060 (prod). Read only when the drawer is about to render.
   */
  dontMissRewardsDrawer: {
    name: "dont-miss-rewards-drawer",
    variants: {
      control: "control",
      rewardDrawer: "reward-drawer",
    },
  },
} as const satisfies Record<string, PosthogExperiment>;

export type Experiment = (typeof EXPERIMENTS)[keyof typeof EXPERIMENTS];
